import { useEffect, useMemo, useRef, useState } from 'react';

import { randomTip } from '@/game/data/loadingTips';
import { arc } from './ThroatRings';

/**
 * The walk in. Shown once, between tapping DIG and the corridor being playable.
 *
 * `MiningLoader` is the cold-start advert — logo, progress bar, ten seconds of
 * "here is what this game is". This is a different moment and wants a different
 * thing: the player has already decided. So there is no logo, no percentage and no
 * branding. It is the last quiet beat before the cave, and its only job is to make
 * the descent feel like it starts somewhere.
 *
 * ## It is rendered, not played
 *
 * The brief for this was a 6-second 1920×1080 Cycles render. That is the right
 * medium for a *trailer* and the wrong one for a loading screen, for one reason
 * that has nothing to do with taste: a video has to download before it can cover
 * a wait, so it makes the wait longer. At ShweTap's 1080p24 that is megabytes, on
 * connections where the whole point of `LOADING_SCREEN.md` was to be honest about
 * how slow things are. The same shot rendered live costs nothing to fetch, starts
 * on the first frame, and cannot be the reason the player is waiting.
 *
 * Everything in the brief that carries the feeling is here — the warm lantern at
 * `#FFB347` (already `--torch-warm`), volumetric haze, drifting dust, the corridor
 * darkening as it recedes, blue crystals surfacing on the walls, a glow in the
 * distance, and a fade to black.
 *
 * ## It is this mine, not a cave
 *
 * The timbering and the rails come from `blenderdoc/caveblender.png` §3, where the
 * corridor's whole silhouette is the wooden frame — two posts, a lintel, braced at
 * the corners — over track running to the vanishing point. A stone tube without them
 * reads as a *cave*; the frame is what makes it a *mine*, and it was the single
 * biggest thing missing from the first pass.
 *
 * The frames live inside the ring groups, so they inherit each ring's scale and get
 * their perspective for free. The rails do not: track is continuous, so it is drawn
 * once, converging on the vanishing point. Each ring also carries one sleeper —
 * ties rushing under the camera are what actually sells forward motion, because the
 * walls alone are too uniform to read as movement.
 *
 * Still not here, and still wanting the real modular cave (`docs/CAVE_VIEW.md`
 * §4.9): corner and intersection pieces, hanging roots, water drips, bats.
 *
 * ## The timing is honest
 *
 * `MiningLoader`'s rule — *"driven by real load steps, never a synthetic timer"* —
 * applies here too, so this does **not** run for a fixed six seconds. It plays while
 * `POST /cave/enter` is in flight and hands over the moment the cave is ready. The
 * only padding is `MIN_BEAT_MS`, which exists so a fast server produces a beat
 * rather than a flash; on a slow one the animation simply keeps going. A player on
 * a good connection should not be made to wait for a cinematic to finish being
 * cinematic.
 */

/** Long enough to read as a shot, short enough not to be a tax on a fast connection. */
export const MIN_BEAT_MS = 1400;
/** The fade to black, after which the corridor is live underneath. */
export const FADE_MS = 420;

/**
 * How much longer to hold the shot once the cave is ready, in ms.
 *
 * This is the whole timing policy, and it is one-directional on purpose: it can
 * only ever *pad* a wait that was too short to read as a shot. It can never extend
 * one that was already long enough, so a player on a bad connection never pays for
 * the cinematic on top of paying for the server. If `/cave/enter` took longer than
 * `MIN_BEAT_MS`, this is 0 and the fade starts on the same tick the run opened.
 */
export function handoffDelayMs(elapsedMs: number): number {
  return Math.max(0, MIN_BEAT_MS - elapsedMs);
}

/** Dust turning in the lantern beam. Fixed set, hashed offsets — no per-frame work. */
const MOTES = Array.from({ length: 16 }, (_, i) => ({
  left: 18 + ((i * 37) % 64),
  top: 34 + ((i * 53) % 48),
  /* Whole multiples of BEAT_S: 5, 6, 7 or 8 beats. Dust on its own clock was the
     one layer that visibly drifted against everything else. */
  beats: 5 + (i % 4),
  delayBeats: i % 5,
  size: 1.4 + ((i * 7) % 3) * 0.5,
}));

/**
 * Crystals on the walls — the brief's blue tell, and the only cold light in the shot.
 *
 * Kept out of the middle third on purpose. The centre of the frame is the throat
 * receding to the vanishing point, so anything drawn there is floating in mid-air
 * rather than sitting on stone; these hug the left and right walls where the arc
 * gives them a surface to belong to.
 */
export const CRYSTALS = [
  { left: 7, top: 52, rot: -16, h: 22, delay: 0.1 },
  { left: 90, top: 46, rot: 13, h: 26, delay: 0.26 },
  { left: 13, top: 70, rot: 7, h: 16, delay: 0.42 },
  { left: 85, top: 72, rot: -10, h: 19, delay: 0.34 },
  { left: 95, top: 62, rot: 20, h: 13, delay: 0.5 },
];

/**
 * The longest a crystal may still be arriving, in seconds.
 *
 * `MIN_BEAT_MS` is 1400 and the fade starts the moment the cave is ready, so on a
 * fast server the whole shot can be 1.4 s. The first pass staggered crystals out to
 * 1.5 s + a 1.6 s fade — the last one finished appearing a second and a half after
 * the screen had gone. Anything that must be *seen* has to have finished by here.
 */
export const REVEAL_BUDGET_S = 0.9;

/**
 * Rings of the throat, as fractions of the authored arc.
 *
 * Each one is the same curve `ThroatRings` draws in the playable view, scaled and
 * pushed outward on a loop — a ring growing past the camera is what "walking
 * forward" looks like from behind the walker. Staggered so the stream never has a
 * gap, and slower than a real step because this is a cinematic, not gameplay.
 */
const RING_COUNT = 6;

/**
 * One footfall, in seconds. **Every cycle in this shot is a whole multiple of it.**
 *
 * The first pass picked each duration independently — 0.62 s walk, 1.24 s lamp, 4.2 s
 * rings, 3.6 s far glow — and they drifted against each other, so the scene never
 * repeated and never landed. Motion that shares no common period reads as several
 * animations playing near each other rather than one place moving. Tied to the beat
 * they phase-lock: the lamp swings once per two steps, the far glow breathes once
 * per five, a ring arrives every seven, and the whole shot returns to its start
 * every 14 beats.
 */
export const BEAT_S = 0.74;
export const RING_CYCLE_S = BEAT_S * 7;
/** Two steps to a full swing — a lantern that swung once per step would be running. */
export const SWING_S = BEAT_S * 2;
export const FAR_PULSE_S = BEAT_S * 5;

/**
 * The scene's own fade-in, in ms.
 *
 * Deliberately shorter than `FADE_MS`. Arriving should feel like a cut to a place
 * that is already there; leaving should feel like the dark closing. Symmetric
 * timings made the entrance feel like a slow dissolve, which is a title card, not
 * a door.
 */
export const IN_MS = 260;

export function CaveEnterCinematic({ ready, onDone }: {
  /** The run is open and the corridor can take over. */
  ready: boolean;
  onDone: () => void;
}) {
  const [leaving, setLeaving] = useState(false);
  const startedAt = useRef(Date.now());
  const tip = useMemo(() => randomTip(), []);
  const done = useRef(false);
  /* Held in a ref so an inline `onDone={() => ...}` at the call site cannot restart
     the handoff: as a dependency it changes identity every render, which would clear
     and re-arm the fade timer forever and leave the cinematic up for good. */
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!ready || done.current) return;
    const wait = handoffDelayMs(Date.now() - startedAt.current);
    /* Both timers are tracked, because the second one is armed inside the first:
       returning a cleanup from a timeout callback does nothing, so an unmount
       during the fade would otherwise still call onDone on a dead component. */
    const timers: number[] = [];

    timers.push(
      window.setTimeout(() => {
        setLeaving(true);
        timers.push(
          window.setTimeout(() => {
            /* Guarded: a second call would unmount the corridor that just mounted. */
            if (done.current) return;
            done.current = true;
            onDoneRef.current();
          }, FADE_MS),
        );
      }, wait),
    );

    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [ready]);

  return (
    <div
      className="absolute inset-0 z-40 overflow-hidden bg-[var(--cave-void)] select-none"
      style={{ opacity: leaving ? 0 : 1, transition: `opacity ${FADE_MS}ms ease-in` }}
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Entering the cave</span>

      {/* Keyframes are co-located for the same reason MiningLoader's are: this can
          render before app CSS has settled, and a loading screen that needs a
          stylesheet to animate is not a loading screen. */}
      <style>{`
        /* Every ring is born at the vanishing point and dies past the camera, so
           its in and out are the same curve seen from both ends: up fast as it
           emerges from the dark, down slowly as it passes the lantern. */
        @keyframes swtRing  { from { transform: scale(.14); opacity: 0 }
                              12%  { opacity: .92 }
                              64%  { opacity: .52 }
                              to   { transform: scale(2.6); opacity: 0 } }
        /* The scene arriving. Not a dissolve — a short lift, so it reads as a cut
           to somewhere that already exists rather than a title card. */
        @keyframes swtIn    { from { opacity: 0; transform: scale(1.035) }
                              to   { opacity: 1; transform: scale(1) } }
        @keyframes swtWalk  { 0%,100% { transform: translate3d(0,0,0) }
                              50%     { transform: translate3d(0,-1.6%,0) } }
        @keyframes swtLamp  { 0%,100% { transform: rotate(-6deg) }
                              50%     { transform: rotate(6deg) } }
        @keyframes swtArm   { 0%,100% { transform: rotate(7deg) }
                              50%     { transform: rotate(-7deg) } }
        @keyframes swtStepA { 0%,100% { transform: translateY(0) }
                              50%     { transform: translateY(-3.2px) } }
        @keyframes swtStepB { 0%,100% { transform: translateY(-3.2px) }
                              50%     { transform: translateY(0) } }
        @keyframes swtGlow  { 0%,100% { opacity: .82 } 50% { opacity: 1 } }
        @keyframes swtMote  { from { transform: translate3d(0,10px,0); opacity: 0 }
                              25%  { opacity: .65 }
                              to   { transform: translate3d(-14px,-46px,0); opacity: 0 } }
        /* In, overshoot, settle — then hand over to a shimmer. A crystal that fades
           to a fixed opacity and stops reads as a decal on the wall. */
        @keyframes swtCryst { from { opacity: 0; transform: scale(.82) }
                              70%  { opacity: 1 }
                              to   { opacity: .88; transform: scale(1) } }
        @keyframes swtShim  { 0%,100% { opacity: .72 } 50% { opacity: 1 } }
        @keyframes swtFar   { 0%,100% { opacity: .32 } 50% { opacity: .66 } }
        .swt-ring { transform-box: view-box; transform-origin: 50% 46%; }
        .swt-lamp { transform-box: fill-box; transform-origin: 50% 0%; }
        .swt-arm  { transform-box: fill-box; transform-origin: 50% 0%; }
        @media (prefers-reduced-motion: reduce) {
          .swt-cine *, .swt-cine *::before, .swt-cine *::after { animation: none !important; }
          /* The rings are invisible at their keyframe start, so freezing them mid-flight
             is not enough — without this the corridor would simply not be there. */
          .swt-ring { transform: scale(var(--rest)); opacity: .55; }
          /* These start at inline opacity 0 and are revealed *by* their animation,
             so stopping it erased them. Inline styles outrank a stylesheet rule,
             hence !important — the alternative is duplicating the reveal in JS. */
          .swt-cryst { opacity: .85 !important; transform: none !important; }
          .swt-mote  { opacity: .6 !important; }
          /* The scene's entrance is an animation too, so without this the whole
             composition stays at its keyframe start — which is opacity 0. */
          .swt-cine  { opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      <div
        className="swt-cine absolute inset-0"
        style={{ animation: `swtIn ${IN_MS}ms ease-out both` }}
      >
        {/* 1. The throat, coming toward the camera.
               Same authored arc as the playable view — one curve, drawn twice — so
               the cave the player is walking into is the cave they arrive in, and
               the handoff at the end of the fade is not a cut to somewhere else. */}
        <svg
          aria-hidden
          className="absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="swtRail" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#FFD9A0" stopOpacity="0.75" />
              <stop offset="55%" stopColor="#FFB347" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#FFB347" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="swtFloor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1A100B" stopOpacity="0" />
              <stop offset="60%" stopColor="#2A1B12" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#3A2617" stopOpacity="0.8" />
            </linearGradient>
          </defs>
          {Array.from({ length: RING_COUNT }, (_, i) => {
            /* Where this ring sits when animation is off, spread across the run of
               the loop so a reduced-motion viewer still sees a receding tunnel. */
            const rest = 0.2 + (i / RING_COUNT) * 1.5;
            return (
              <g
                key={i}
                className="swt-ring"
                style={{
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  ['--rest' as string]: String(rest),
                  animation: `swtRing ${RING_CYCLE_S}s linear ${(i * RING_CYCLE_S) / RING_COUNT}s infinite`,
                }}
              >
                {/* The arc is authored in its own box from the origin, so shift it
                    up to put the vanishing point at 46% — the projection's constant. */}
                <g transform="translate(0 -4)">
                  <path
                    d={arc(100, 100)}
                    fill="var(--cave-wall)"
                    fillOpacity={0.5}
                    stroke="var(--cave-rim)"
                    strokeWidth={0.4}
                    strokeOpacity={0.5}
                  />
                  {/* Timbering. In caveblender.png §3 the corridor's whole silhouette
                      is the wooden frame — two posts and a lintel, braced at the
                      corners — and without it a stone tube reads as a cave rather
                      than a mine. Inside the ring group, so it takes the ring's
                      scale and gets its perspective for free. */}
                  {/* Timber is a SILHOUETTE, not an object. The first pass filled it
                      with mid-brown and it became the brightest thing on screen — a
                      pale box in front of the cave. Painting it near-black then
                      erased it entirely — the wall behind is just as dark. What
                      works is a value close to the wall plus a lit leading edge, so
                      the frame is read from its highlights rather than its fill.
                      Every other ring, so the shaft is spaced like real supports
                      rather than a solid tunnel of wood. */}
                  {i % 2 === 0 && (
                    <>
                      <g fill="#4A3524" fillOpacity="0.95">
                        <rect x="9" y="26" width="5.4" height="70" rx="1" />
                        <rect x="85.6" y="26" width="5.4" height="70" rx="1" />
                        <rect x="6" y="24" width="88" height="6" rx="1.4" />
                        {/* Corner braces — the diagonal that reads as carpentry. */}
                        <path d="M14.4 34 L24 30 L24 34.4 L14.4 38.4 Z" />
                        <path d="M85.6 34 L76 30 L76 34.4 L85.6 38.4 Z" />
                      </g>
                      {/* The single lit edge. This is the whole reason the beam reads
                          as wood rather than as a hole cut in the picture. */}
                      <g stroke="#C08A3E" strokeOpacity="0.7" strokeWidth="0.7" fill="none">
                        <path d="M9 26 H14.4 M85.6 26 H91" />
                        <path d="M6 24 H94" />
                        {/* Inner edge of each post. The posts are the same value as
                            the wall behind them, so without a lit edge they are
                            invisible — dark on dark, the same mistake the floor-rock
                            contact shadow made before it became a warm bounce. */}
                        <path d="M14.4 30 V94" strokeOpacity="0.4" />
                        <path d="M85.6 30 V94" strokeOpacity="0.4" />
                      </g>
                    </>
                  )}

                  {/* One sleeper per ring. Ties rushing under the camera are what
                      actually sells forward motion — the walls alone are too uniform
                      to read as movement. Dark: a tie is timber lying in shadow. */}
                  <rect x="35" y="93" width="30" height="1.8" rx="0.5"
                        fill="#2A1E14" fillOpacity="0.85" />
                </g>
              </g>
            );
          })}

          {/* Rails, drawn once rather than per ring: track is continuous, so it
              converges on the vanishing point instead of scaling with a ring. */}
          {/* The ground the track lies on. Without it the rails were two lines in
              the dark and the eye read them as a ladder, not a floor. */}
          <rect x="0" y="62" width="100" height="38" fill="url(#swtFloor)" />

          {/* Rails, drawn once rather than per ring: track is continuous, so it
              converges on the vanishing point instead of scaling with a ring.
              They STOP at y=64 rather than running to the vanishing point at 46 —
              carried all the way up they crossed the character and climbed into the
              air behind him, which is exactly what a ladder looks like. */}
          <g fill="none" strokeLinecap="round">
            <path d="M42.8 100 L48.6 64" stroke="#6A5C4C" strokeWidth="0.7" opacity="0.4" />
            <path d="M57.2 100 L51.4 64" stroke="#6A5C4C" strokeWidth="0.7" opacity="0.4" />
            {/* The torch catching the rail head. A dark metal line on a dark floor is
                invisible — the same finding as the floor rocks, where what reads as
                contact is light, not shadow. Brightest near the lantern and gone
                before the far end, so it doubles as a depth cue. */}
            <path d="M42.8 100 L48.6 64" stroke="url(#swtRail)" strokeWidth="0.35" />
            <path d="M57.2 100 L51.4 64" stroke="url(#swtRail)" strokeWidth="0.35" />
          </g>
        </svg>

        {/* 2. Volumetric haze. Two stacked radials: the lantern's near warmth, and
               the cold falloff that makes the far end read as further rather than
               merely darker. */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(58% 42% at 50% 68%, rgba(255,179,71,.20) 0%, rgba(255,179,71,.06) 40%, rgba(0,0,0,0) 74%),' +
              'radial-gradient(42% 34% at 50% 44%, rgba(90,140,190,.10) 0%, rgba(0,0,0,0) 70%)',
          }}
        />

        {/* 3. The light at the far end — the reason to keep walking. */}
        <div
          aria-hidden
          className="absolute left-1/2 top-[45%] h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,214,140,.55), rgba(255,179,71,0) 68%)',
            animation: `swtFar ${FAR_PULSE_S}s ease-in-out infinite`,
          }}
        />

        {/* 4. Crystals surfacing on the walls. */}
        {CRYSTALS.map((c, i) => (
          <span
            key={i}
            aria-hidden
            className="swt-cryst absolute block"
            style={{
              left: `${c.left}%`,
              top: `${c.top}%`,
              width: c.h * 0.42,
              height: c.h,
              opacity: 0,
              transform: `rotate(${c.rot}deg)`,
              clipPath: 'polygon(50% 0%, 100% 34%, 74% 100%, 26% 100%, 0% 34%)',
              background: 'linear-gradient(180deg, #BFE4FF, #4E86C8 62%, #1E3A5F)',
              boxShadow: '0 0 12px rgba(120,180,255,.5)',
              /* Reveal, then shimmer — two animations on one element, the second
                 starting exactly where the first ends so there is no seam. Both
                 finish arriving inside REVEAL_BUDGET_S. */
              animation:
                `swtCryst ${REVEAL_BUDGET_S - c.delay}s ease-out ${c.delay}s forwards,`
                + ` swtShim ${FAR_PULSE_S}s ease-in-out ${REVEAL_BUDGET_S}s infinite`,
            }}
          />
        ))}

        {/* 5. Dust in the beam. */}
        {MOTES.map((m, i) => (
          <span
            key={i}
            aria-hidden
            className="swt-mote absolute block rounded-full bg-[var(--torch-core)]"
            style={{
              left: `${m.left}%`,
              top: `${m.top}%`,
              width: m.size,
              height: m.size,
              opacity: 0,
              animation:
                `swtMote ${m.beats * BEAT_S}s linear ${m.delayBeats * BEAT_S}s infinite`,
            }}
          />
        ))}

        {/* 6. Jin, from behind, lantern swinging with the walk.
               Drawn as one SVG rather than a stack of divs so the glow is anchored
               to the lantern that casts it — the first pass had a warm blob floating
               beside him with no object in it, which read as a bug and not a lamp.
               Inline rather than sprited so the screen owes nothing to the atlas it
               is waiting on. He carries it in his right hand, which from behind is
               the viewer's left. */}
        <div
          aria-hidden
          className="absolute bottom-[19%] left-1/2 w-[30%] max-w-[132px] -translate-x-1/2"
        >
          {/* Centring is on the parent and the bob is on the child, because both
              want the `transform` property and a running animation wins it outright.
              With them on one element, `prefers-reduced-motion` killed the animation
              and took the -50% centring with it — Jin walked off to the right. */}
          <div style={{ animation: `swtWalk ${BEAT_S}s ease-in-out infinite` }}>
          <svg viewBox="0 0 64 84" className="h-auto w-full overflow-visible">
            <defs>
              <radialGradient id="swtLampGlow">
                <stop offset="0%" stopColor="#FFE6A8" stopOpacity=".95" />
                <stop offset="42%" stopColor="#FFB347" stopOpacity=".42" />
                <stop offset="100%" stopColor="#FFB347" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="swtPool">
                <stop offset="0%" stopColor="#FFBA66" stopOpacity=".34" />
                <stop offset="100%" stopColor="#FFBA66" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* The floor the lantern reaches. Warm, not dark: on a near-black floor
                what reads as ground is light, the same finding as the field rocks. */}
            <ellipse cx="32" cy="80" rx="30" ry="7" fill="url(#swtPool)" />
            <ellipse cx="32" cy="79" rx="11" ry="2.6" fill="rgba(0,0,0,.5)" />

            {/* Seen from behind, so no face — the helmet lamp is a spill of light
                past his head, not a dot looking back at you.

                Legs alternate on the same 0.74 s clock as the body bob, one at half
                a cycle's offset. A miner who bobs but never takes a step reads as
                hovering, which is what the first pass looked like. */}
            <g className="swt-legL" style={{ animation: `swtStepA ${BEAT_S}s ease-in-out infinite` }}>
              <rect x="24.6" y="61" width="6.4" height="15" rx="2.8" fill="#4A3524" />
              <rect x="23.6" y="73.6" width="8.6" height="4.4" rx="1.9" fill="#2E2116" />
            </g>
            <g className="swt-legR" style={{ animation: `swtStepB ${BEAT_S}s ease-in-out infinite` }}>
              <rect x="33" y="61" width="6.4" height="15" rx="2.8" fill="#4A3524" />
              <rect x="31.8" y="73.6" width="8.6" height="4.4" rx="1.9" fill="#2E2116" />
            </g>

            {/* Torso, then the pack on top of it. The pack is narrower than the
                shoulders so the shirt still shows at the sides — a pack the full
                width of the body just reads as a second torso. */}
            <rect x="20.6" y="33" width="22.8" height="31" rx="8.4" fill="#C9A063" />
            <path d="M22 40c3.4 2.6 16.6 2.6 20 0" stroke="#A8834C" strokeWidth="1.1" fill="none" />
            <rect x="24" y="30.5" width="16" height="26" rx="7" fill="#5E4834" />
            <rect x="27" y="35" width="10" height="7" rx="2" fill="#4A3524" />
            {/* Pack straps over the shoulders, which is what makes it a pack. */}
            <path d="M26.4 33.4 L28.6 44" stroke="#4A3524" strokeWidth="1.7" fill="none" />
            <path d="M37.6 33.4 L35.4 44" stroke="#4A3524" strokeWidth="1.7" fill="none" />

            {/* The free arm, swinging opposite the lantern hand. */}
            <g className="swt-arm" style={{ animation: `swtArm ${SWING_S}s ease-in-out infinite` }}>
              <rect x="42.6" y="36" width="3.4" height="15" rx="1.7" fill="#C9A063" />
              <circle cx="44.3" cy="52" r="2.3" fill="#8A6A4A" />
            </g>

            {/* The red neckerchief — the one thing that says Jin and not "a miner". */}
            <path d="M26 32h12l-1.6 4.2h-8.8z" fill="#B33A2B" />

            {/* Hair, not skin. He is walking away, so what faces the camera is the
                back of his head; a skin-toned sphere here read as a blank face
                turned around to look at you. */}
            <circle cx="32" cy="22" r="10" fill="#4A2E1A" />
            <path d="M22 22a10 10 0 0 1 20 0z" fill="#6B4A2A" />
            <ellipse cx="32" cy="22.5" rx="12" ry="2.4" fill="#553A20" />
            {/* Rim light down his left side, thrown by the lantern he is carrying.
                Without it he is a flat shape pasted over a lit corridor. */}
            <path d="M22.4 28c-1.2 8 -0.6 21 1.2 30" stroke="#FFC98A" strokeWidth="1.2"
                  fill="none" opacity="0.5" strokeLinecap="round" />
            <path d="M23.2 15.4a10 10 0 0 0 -1 6.6" stroke="#FFC98A" strokeWidth="1.1"
                  fill="none" opacity="0.4" strokeLinecap="round" />
            {/* Helmet lamp, spilling forward past him rather than at the camera. */}
            <ellipse cx="32" cy="14" rx="9.5" ry="5" fill="url(#swtLampGlow)" opacity=".62" />

            {/* The lantern arm. Rotates about the shoulder, so the lamp and its
                light swing together instead of sliding apart. */}
            <g className="swt-lamp" style={{ animation: `swtLamp ${SWING_S}s ease-in-out infinite` }}>
              <rect x="17.4" y="36" width="3.2" height="16" rx="1.6" fill="#8A6A4A" />
              <path d="M15 52h8" stroke="#6B4E33" strokeWidth="1.4" fill="none" />
              <path d="M15.4 56a3.6 3.6 0 0 1 7.2 0" stroke="#C08A3E" strokeWidth="1.2" fill="none" />
              <circle
                cx="19"
                cy="60"
                r="17"
                fill="url(#swtLampGlow)"
                style={{ animation: `swtGlow ${SWING_S}s ease-in-out infinite` }}
              />
              <path d="M15.6 56.4h6.8l1.4 8H14.2z" fill="#7A5A34" />
              <rect x="16.6" y="57.6" width="4.8" height="5.4" rx="1" fill="#FFE6A8" />
            </g>
          </svg>
          </div>
        </div>

        {/* 7. Vignette — the darkness is the antagonist, so it closes in. */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(64% 52% at 50% 56%, rgba(0,0,0,0) 40%, rgba(0,0,0,.8) 100%)',
          }}
        />
      </div>

      {/* The only words. A tip, because the player is about to need one. */}
      <div className="absolute inset-x-0 bottom-[6%] px-8 text-center">
        <p className="font-pixel text-[9px] uppercase tracking-[0.22em] text-[var(--torch-warm)]">
          Entering the cave
        </p>
        <p className="mx-auto mt-2 max-w-xs text-[11px] leading-relaxed text-ink-dim">{tip}</p>
      </div>
    </div>
  );
}
